import React, { useCallback, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import withStyles from '@mui/styles/withStyles';
import TableImport from 'components/TableImport';
import ResultDialog from 'components/ResultDialog';
import Content from 'studio/components/Content/Content';
import { useLoader } from 'components/loaders/FadeLoader';
import StorageIcon from '@mui/icons-material/StorageRounded';
import { useMessage } from 'studio/components/Message';
import styles from './Import.styles';
import Typography from '@mui/material/Typography';
import { trackEvent } from 'utils/eventTracking';
import services from '../../../services';
import PageTitle from 'studio/components/PageTitle';

const Import = ({ classes, knowledgeGraph, onResultDialogClosed, editorRef }) => {
  useEffect(() => {
    trackEvent('Studio Select Data Opened');
  }, []);

  const [databases, setDatabases] = useState([]);
  const [databaseConnection, setDatabaseConnection] = useState(null);
  const [importFunction, setImportFunction] = useState(undefined);
  const [uploadResult, setUploadResult] = useState(null);

  const { errorMessage } = useMessage();
  const { loading, withLoader } = useLoader();

  useEffect(() => {
    const fetchDatabases = async () => {
      const result = await withLoader(services.getConnectors());
      if (result.success) {
        const databases = result.data.data.map(({ data }) => data).filter(({ type }) => type === 'DATABASE_CONNECTOR');

        setDatabases(databases);
        return;
      }
      const message = result?.response?.data?.message || 'There was an error fetching databases.';
      errorMessage(message);
    };

    const fetchDatabaseConnection = async () => {
      const result = await withLoader(services.getDatabaseConnection(knowledgeGraph.data.id));
      if (result.success) {
        setDatabaseConnection(result.data);
      } else {
        const message =
          result?.response?.data?.data?.message || 'An error occurred while getting the database connection.';
        errorMessage(message);
      }
    };

    fetchDatabases();
    // Do not try to fetch DB connection for example for Excel KGs, the user doesn't have access
    if (knowledgeGraph && !knowledgeGraph?.data?.isDatabaseManagedByVeezoo) {
      fetchDatabaseConnection();
    }
  }, [knowledgeGraph]);

  const handleClose = useCallback(() => {
    setUploadResult(null);
    onResultDialogClosed?.();
  }, [knowledgeGraph, setUploadResult]);

  const importTables = useCallback(async () => {
    const response = await withLoader(importFunction());
    if (response.success) {
      setUploadResult(response.data);
      // reload the editor's object and file trees
      editorRef.current.reloadTrees();
    } else {
      const message = response?.response?.data?.data?.message || 'An error occurred while analyzing tables.';
      errorMessage(message);
    }
  }, [knowledgeGraph, importFunction, withLoader, errorMessage]);

  const handleFetchingError = useCallback((response, message) => errorMessage(message), [errorMessage]);

  return (
    <Content>
      <div className={classes.importContainer}>
        <PageTitle icon={StorageIcon}>Select Data</PageTitle>
        <Typography variant="body1" gutterBottom>
          Select the tables and columns you want to ask questions about.
        </Typography>

        <TableImport
          usesSchemas={databases.find(db => db.id === databaseConnection?.databaseSystemId)?.usesSchemas}
          disabled={loading}
          knowledgeGraph={knowledgeGraph.data}
          setImportFunction={setImportFunction}
          handleFetchingError={handleFetchingError}
        />
      </div>
      <div className={classes.actions}>
        <Button
          disabled={loading || importFunction === undefined}
          variant="contained"
          color="primary"
          onClick={importTables}
        >
          Import
        </Button>
      </div>

      <ResultDialog uploadResult={uploadResult} handleClose={handleClose} />
    </Content>
  );
};

export default withStyles(styles)(Import);
